import React, { useMemo, useState } from "react";
import {
  Badge,
  Card,
  Checkbox,
  Modal,
  Spinner,
  TextInput,
  Tooltip,
} from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  HiOutlineBookOpen,
  HiOutlineClipboardCopy,
  HiOutlineSearch,
  HiOutlineTrash,
} from "react-icons/hi";

import SubTitle from "../../components/SubTitle";
import Button from "../../components/Button";
import Notification from "../../components/Notification";
import ToastNotification from "../../components/ToastNotification";
import { ToastType } from "../../typesData/ToastNotificationTypes";
import {
  addMemberBooks,
  getBookCatalog,
  getMemberBooks,
  removeMemberBooks,
} from "../../api/memberBooks/actions";
import { CatalogBook, MemberBook } from "../../api/memberBooks/types";
import {
  memberBooksStrings as S,
  formatMessage,
} from "./MemberBooksTab.strings";

interface MemberBooksTabProps {
  customerId: string;
  email: string;
  status?: string;
}

type Banner = { type: ToastType; message: string };

const SectionCard: React.FC<{
  heading: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ heading, children, testId }) => (
  <Card {...(testId ? { "data-testid": testId } : {})}>
    <div className="pb-3 mb-3 border-b border-gray-100">
      <SubTitle>{heading}</SubTitle>
    </div>
    {children}
  </Card>
);

const MemberBooksTab: React.FC<MemberBooksTabProps> = ({
  customerId,
  email,
  status,
}) => {
  const isActive = (status ?? "").toLowerCase() === "active";
  const queryClient = useQueryClient();
  const booksKey = ["member-books", customerId];

  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [removeTarget, setRemoveTarget] = useState<MemberBook | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);

  const booksQuery = useQuery(booksKey, () => getMemberBooks(customerId), {
    enabled: Boolean(customerId),
  });
  const catalogQuery = useQuery("book-catalog", getBookCatalog);

  const ownedCodes = useMemo(
    () => new Set((booksQuery.data?.books ?? []).map((b) => b.code)),
    [booksQuery.data]
  );

  const availableBooks = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (catalogQuery.data ?? [])
      .filter((b: CatalogBook) => b.isActive && !ownedCodes.has(b.code))
      .filter((b: CatalogBook) => !term || b.name.toLowerCase().includes(term))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogQuery.data, ownedCodes, filter]);

  const refetchBooks = () => queryClient.invalidateQueries(booksKey);

  const addMutation = useMutation(
    (codes: string[]) => addMemberBooks(customerId, codes),
    {
      onSuccess: (_data, codes) => {
        setSelected(new Set());
        const message = formatMessage(S.add.success, {
          count: codes.length,
          email,
        });
        setBanner({ type: "SUCCESS", message });
        ToastNotification({ type: "success", message });
        void refetchBooks();
      },
      onError: () => setBanner({ type: "FAILURE", message: S.add.error }),
    }
  );

  const removeMutation = useMutation(
    (book: MemberBook) => removeMemberBooks(customerId, [book.code]),
    {
      onSuccess: (_data, book) => {
        setRemoveTarget(null);
        const message = formatMessage(S.removeConfirm.success, {
          name: book.name ?? book.code,
          email,
        });
        setBanner({ type: "SUCCESS", message });
        ToastNotification({ type: "success", message });
        void refetchBooks();
      },
      onError: () => {
        setRemoveTarget(null);
        setBanner({ type: "FAILURE", message: S.removeConfirm.error });
      },
    }
  );

  const isBusy = addMutation.isLoading || removeMutation.isLoading;

  const toggleSelected = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const hasSubscription = booksQuery.data?.hasSubscription ?? false;
  const currentBooks = booksQuery.data?.books ?? [];

  return (
    <div className="flex flex-col gap-6" data-testid="member-books-tab">
      {!isActive && <Notification type="OTHER" message={S.inactive} />}
      {booksQuery.isError && (
        <Notification type="FAILURE" message={S.loadError} />
      )}
      {booksQuery.data && !hasSubscription && (
        <Notification type="OTHER" message={S.noSubscription} />
      )}
      {banner && <Notification type={banner.type} message={banner.message} />}

      <SectionCard
        heading={
          booksQuery.data
            ? formatMessage(S.current.headingWithCount, {
                count: currentBooks.length,
              })
            : S.current.heading
        }
        testId="member-books-current"
      >
        {booksQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : currentBooks.length === 0 ? (
          <p className="text-sm text-gray-500">{S.current.empty}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentBooks.map((book) => (
              <div
                key={book.code}
                className="flex items-center justify-between gap-3 rounded-md border border-gray-100 p-3"
                data-testid="member-book-row"
              >
                <div className="min-w-0">
                  {book.resolved ? (
                    <>
                      <p className="font-medium truncate">{book.name}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500">
                          {book.code}
                        </span>
                        <Tooltip content={S.current.copyCodeTooltip}>
                          <CopyToClipboard
                            text={book.code}
                            onCopy={() =>
                              ToastNotification({
                                type: "success",
                                message: S.current.codeCopied,
                              })
                            }
                          >
                            <button
                              type="button"
                              aria-label={S.current.copyCodeTooltip}
                              className="text-gray-400 hover:text-neutral-800 cursor-pointer"
                            >
                              <HiOutlineClipboardCopy className="h-3.5 w-3.5" />
                            </button>
                          </CopyToClipboard>
                        </Tooltip>
                      </div>
                    </>
                  ) : (
                    <p className="font-mono text-xs font-medium text-neutral-700">
                      {book.code}
                    </p>
                  )}
                </div>
                {book.resolved ? (
                  <Tooltip content={S.current.remove}>
                    <button
                      type="button"
                      aria-label={formatMessage(S.current.removeAria, {
                        name: book.name ?? book.code,
                      })}
                      className="text-gray-500 hover:text-error-500 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={!isActive || !hasSubscription || isBusy}
                      onClick={() => setRemoveTarget(book)}
                    >
                      <HiOutlineTrash className="h-4 w-4" />
                    </button>
                  </Tooltip>
                ) : (
                  <Tooltip content={S.current.systemBadgeHint}>
                    <span className="shrink-0">
                      <Badge color="gray" size="sm">
                        {S.current.systemBadge}
                      </Badge>
                    </span>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard heading={S.add.heading} testId="member-books-add">
        {catalogQuery.isError ? (
          <Notification type="FAILURE" message={S.add.catalogError} />
        ) : (
          <>
            <div className="max-w-xl">
              <TextInput
                icon={HiOutlineSearch}
                aria-label={S.add.filterLabel}
                placeholder={S.add.filterPlaceholder}
                value={filter}
                disabled={!isActive || !hasSubscription}
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>

            <div className="mt-4 max-h-96 overflow-y-auto pr-1">
              {catalogQuery.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : availableBooks.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {filter.trim() ? S.add.noMatches : S.add.noneAvailable}
                </p>
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
                  {availableBooks.map((book) => (
                    <li key={book.code}>
                      <label className="flex items-center gap-3 rounded-md p-2 hover:bg-gray-50 cursor-pointer min-w-0">
                        <Checkbox
                          className="shrink-0"
                          checked={selected.has(book.code)}
                          disabled={!isActive || !hasSubscription}
                          onChange={() => toggleSelected(book.code)}
                        />
                        <span className="min-w-0">
                          <span className="block font-medium truncate">
                            {book.name}
                          </span>
                          <span className="block font-mono text-xs text-gray-500 truncate">
                            {book.code}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Button
                variant="PRIMARY"
                disabled={
                  !isActive ||
                  !hasSubscription ||
                  selected.size === 0 ||
                  isBusy
                }
                isProcessing={addMutation.isLoading}
                onClick={() => addMutation.mutate(Array.from(selected))}
              >
                {S.add.submit}
              </Button>
              {selected.size > 0 && (
                <>
                  <span className="text-sm text-gray-500">
                    {formatMessage(S.add.selectedCount, {
                      count: selected.size,
                    })}
                  </span>
                  <button
                    type="button"
                    className="text-sm text-primary-500 hover:underline"
                    onClick={() => setSelected(new Set())}
                  >
                    {S.add.clear}
                  </button>
                </>
              )}
            </div>
            {isActive && hasSubscription && selected.size === 0 && (
              <p className="mt-2 text-xs text-gray-500">{S.add.disabledHint}</p>
            )}
          </>
        )}
      </SectionCard>

      <Modal
        show={Boolean(removeTarget)}
        size="md"
        onClose={() => setRemoveTarget(null)}
      >
        <Modal.Header>
          <span className="flex items-center gap-2">
            <HiOutlineBookOpen className="h-5 w-5" />
            {S.removeConfirm.title}
          </span>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-700">
            {removeTarget &&
              formatMessage(S.removeConfirm.body, {
                name: removeTarget.name ?? removeTarget.code,
                code: removeTarget.code,
                email,
              })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="PRIMARY"
            isProcessing={removeMutation.isLoading}
            disabled={removeMutation.isLoading}
            onClick={() => removeTarget && removeMutation.mutate(removeTarget)}
          >
            {S.removeConfirm.yes}
          </Button>
          <Button
            variant="LIGHT"
            disabled={removeMutation.isLoading}
            onClick={() => setRemoveTarget(null)}
          >
            {S.removeConfirm.no}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MemberBooksTab;
